/**
 * Performance Optimization Dashboard - Real-time monitoring for load testing optimizations
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Card,
  Grid,
  Group,
  Progress,
  Stack,
  Text,
  ThemeIcon,
  Title,
} from '@mantine/core';
import {
  IconActivity,
  IconAlertTriangle,
  IconBolt,
  IconChartLine,
  IconDatabase,
  IconRefresh,
  IconShield,
  IconUsers,
} from '@tabler/icons-react';

interface PerformanceMetrics {
  authCacheHitRate: number;
  authValidationTimeMs: number;
  authCircuitBreakerTrips: number;
  rateLimitUtilization: number;
  rateLimitBlockRate: number;
  rateLimitRedisErrors: number;
  dbConnectionPoolUtilization: number;
  dbAvgQueryLatency: number;
  dbQueryErrors: number;
  graphqlCacheHitRate: number;
  graphqlAvgQueryTime: number;
  graphqlComplexityViolations: number;
  totalThroughputRps: number;
  errorRate: number;
  avgResponseTime: number;
  concurrentUsers: number;
  requestsPerSecond: number;
  memoryUsageMB: number;
  cpuUsagePercent: number;
}

interface PerformanceOptimizationDashboardProps {
  metricsEndpoint?: string;
  refreshInterval?: number;
  thresholds?: {
    errorRateThreshold: number;
    responseTimeThreshold: number;
    cacheHitRateThreshold: number;
    connectionPoolThreshold: number;
  };
  onPerformanceAlert?: (alert: string, severity: 'warning' | 'error') => void;
}

const defaultThresholds = {
  errorRateThreshold: 5.0,
  responseTimeThreshold: 1000,
  cacheHitRateThreshold: 80.0,
  connectionPoolThreshold: 80.0,
};

export const PerformanceOptimizationDashboard: React.FC<PerformanceOptimizationDashboardProps> = ({
  metricsEndpoint = '/api/performance/metrics',
  refreshInterval = 5,
  thresholds = defaultThresholds,
  onPerformanceAlert,
}) => {
  const [metrics, setMetrics] = useState<PerformanceMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [alerts, setAlerts] = useState<Array<{ message: string; severity: 'warning' | 'error' }>>([]);

  const fetchMetrics = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(metricsEndpoint);
      if (!response.ok) throw new Error('Failed to fetch metrics');
      
      const data: PerformanceMetrics = await response.json();
      setMetrics(data);
      setError(null);
      
      const newAlerts: Array<{ message: string; severity: 'warning' | 'error' }> = [];
      
      if (data.errorRate > thresholds.errorRateThreshold) {
        const errorRateStr = data.errorRate.toFixed(1);
        const alert = `High error rate: ${errorRateStr}%`;
        newAlerts.push({ message: alert, severity: 'error' });
        onPerformanceAlert?.(alert, 'error');
      }
      
      if (data.avgResponseTime > thresholds.responseTimeThreshold) {
        const alert = `High response time: ${data.avgResponseTime}ms`;
        newAlerts.push({ message: alert, severity: 'warning' });
        onPerformanceAlert?.(alert, 'warning');
      }
      
      if (data.authCacheHitRate < thresholds.cacheHitRateThreshold) {
        const cacheRateStr = data.authCacheHitRate.toFixed(1);
        const alert = `Low auth cache hit rate: ${cacheRateStr}%`;
        newAlerts.push({ message: alert, severity: 'warning' });
        onPerformanceAlert?.(alert, 'warning');
      }
      
      if (data.dbConnectionPoolUtilization > thresholds.connectionPoolThreshold) {
        const poolUtilStr = data.dbConnectionPoolUtilization.toFixed(1);
        const alert = `High database connection pool utilization: ${poolUtilStr}%`;
        newAlerts.push({ message: alert, severity: 'error' });
        onPerformanceAlert?.(alert, 'error');
      }
      
      setAlerts(newAlerts);
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, [metricsEndpoint, thresholds, onPerformanceAlert]);

  useEffect(() => {
    fetchMetrics();
    const interval = setInterval(fetchMetrics, refreshInterval * 1000);
    return () => clearInterval(interval);
  }, [fetchMetrics, refreshInterval]);

  const getStatusColor = (value: number, threshold: number, isInverse = false): string => {
    const isGood = isInverse ? value < threshold : value > threshold;
    if (isGood) return 'green';
    if (isInverse ? value > threshold * 1.5 : value < threshold * 0.5) return 'red';
    return 'yellow';
  };

  if (loading && !metrics) {
    return (
      <Card p="md">
        <Text>Loading performance metrics...</Text>
      </Card>
    );
  }

  if (error) {
    return (
      <Alert icon={<IconAlertTriangle size="1rem" />} title="Error" color="red">
        {error}
        <Group mt="sm">
          <Button size="xs" onClick={fetchMetrics}>Retry</Button>
        </Group>
      </Alert>
    );
  }

  if (!metrics) return null;

  const formatNumber = (num: number, decimals = 1): string => {
    return num.toFixed(decimals);
  };

  return (
    <Stack gap="md">
      <Group justify="space-between" ta="center">
        <Title order={2}>Performance Optimization Dashboard</Title>
        <Group gap="xs">
          <Badge color="green" variant="dot">
            Live Monitoring
          </Badge>
          <ActionIcon variant="subtle" onClick={fetchMetrics}>
            <IconRefresh size={16} />
          </ActionIcon>
        </Group>
      </Group>

      {alerts.length > 0 && (
        <Stack gap="xs">
          {alerts.map((alert, index) => (
            <Alert
              key={index}
              icon={<IconAlertTriangle size="1rem" />}
              title="Performance Alert"
              color={alert.severity === 'error' ? 'red' : 'yellow'}
            >
              {alert.message}
            </Alert>
          ))}
        </Stack>
      )}

      <Grid>
        <Grid.Col span={3}>
          <Card withBorder p="md">
            <Group justify="space-between">
              <Stack gap={2}>
                <Text size="xs" c="dimmed">Throughput</Text>
                <Text size="xl" fw={600}>{formatNumber(metrics.totalThroughputRps)} RPS</Text>
              </Stack>
              <ThemeIcon color="blue" variant="light">
                <IconBolt size={20} />
              </ThemeIcon>
            </Group>
          </Card>
        </Grid.Col>

        <Grid.Col span={3}>
          <Card withBorder p="md">
            <Group justify="space-between">
              <Stack gap={2}>
                <Text size="xs" c="dimmed">Error Rate</Text>
                <Text 
                  size="xl" 
                  fw={600}
                  c={getStatusColor(metrics.errorRate, thresholds.errorRateThreshold, true)}
                >
                  {formatNumber(metrics.errorRate, 2)}%
                </Text>
              </Stack>
              <ThemeIcon 
                color={getStatusColor(metrics.errorRate, thresholds.errorRateThreshold, true)} 
                variant="light"
              >
                <IconAlertTriangle size={20} />
              </ThemeIcon>
            </Group>
          </Card>
        </Grid.Col>

        <Grid.Col span={3}>
          <Card withBorder p="md">
            <Group justify="space-between">
              <Stack gap={2}>
                <Text size="xs" c="dimmed">Avg Response Time</Text>
                <Text 
                  size="xl" 
                  fw={600}
                  c={getStatusColor(metrics.avgResponseTime, thresholds.responseTimeThreshold, true)}
                >
                  {metrics.avgResponseTime}ms
                </Text>
              </Stack>
              <ThemeIcon color="orange" variant="light">
                <IconChartLine size={20} />
              </ThemeIcon>
            </Group>
          </Card>
        </Grid.Col>

        <Grid.Col span={3}>
          <Card withBorder p="md">
            <Group justify="space-between">
              <Stack gap={2}>
                <Text size="xs" c="dimmed">Concurrent Users</Text>
                <Text size="xl" fw={600}>{metrics.concurrentUsers}</Text>
              </Stack>
              <ThemeIcon color="grape" variant="light">
                <IconUsers size={20} />
              </ThemeIcon>
            </Group>
          </Card>
        </Grid.Col>
      </Grid>

      <Grid>
        <Grid.Col span={6}>
          <Card withBorder p="md">
            <Stack gap="md">
              <Group justify="space-between">
                <Text fw={500}>Authentication Performance</Text>
                <ThemeIcon color="blue" variant="light" size="sm">
                  <IconShield size={16} />
                </ThemeIcon>
              </Group>
              
              <Stack gap="xs">
                <Group justify="space-between">
                  <Text size="sm">Cache Hit Rate</Text>
                  <Text 
                    size="sm" 
                    fw={500}
                    c={getStatusColor(metrics.authCacheHitRate, thresholds.cacheHitRateThreshold)}
                  >
                    {formatNumber(metrics.authCacheHitRate)}%
                  </Text>
                </Group>
                <Progress 
                  value={metrics.authCacheHitRate} 
                  color={getStatusColor(metrics.authCacheHitRate, thresholds.cacheHitRateThreshold)}
                />
                
                <Group justify="space-between">
                  <Text size="sm">Validation Time</Text>
                  <Text size="sm" fw={500}>{formatNumber(metrics.authValidationTimeMs)}ms</Text>
                </Group>
                
                <Group justify="space-between">
                  <Text size="sm">Circuit Breaker Trips</Text>
                  <Text size="sm" fw={500} c={metrics.authCircuitBreakerTrips > 0 ? 'red' : 'green'}>
                    {metrics.authCircuitBreakerTrips}
                  </Text>
                </Group>
              </Stack>
            </Stack>
          </Card>
        </Grid.Col>

        <Grid.Col span={6}>
          <Card withBorder p="md">
            <Stack gap="md">
              <Group justify="space-between">
                <Text fw={500}>Database Performance</Text>
                <ThemeIcon color="green" variant="light" size="sm">
                  <IconDatabase size={16} />
                </ThemeIcon>
              </Group>
              
              <Stack gap="xs">
                <Group justify="space-between">
                  <Text size="sm">Connection Pool</Text>
                  <Text 
                    size="sm" 
                    fw={500}
                    c={getStatusColor(metrics.dbConnectionPoolUtilization, thresholds.connectionPoolThreshold, true)}
                  >
                    {formatNumber(metrics.dbConnectionPoolUtilization)}%
                  </Text>
                </Group>
                <Progress 
                  value={metrics.dbConnectionPoolUtilization} 
                  color={getStatusColor(metrics.dbConnectionPoolUtilization, thresholds.connectionPoolThreshold, true)}
                />
                
                <Group justify="space-between">
                  <Text size="sm">Avg Query Latency</Text>
                  <Text size="sm" fw={500}>{formatNumber(metrics.dbAvgQueryLatency)}ms</Text>
                </Group>
                
                <Group justify="space-between">
                  <Text size="sm">Query Errors</Text>
                  <Text size="sm" fw={500} c={metrics.dbQueryErrors > 0 ? 'red' : 'green'}>
                    {metrics.dbQueryErrors}
                  </Text>
                </Group>
              </Stack>
            </Stack>
          </Card>
        </Grid.Col>
      </Grid>
    </Stack>
  );
};

export default PerformanceOptimizationDashboard;
